import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { 
  ChevronDown, ChevronRight, RefreshCw, 
  FileText, Check
} from 'lucide-react';
import { matchTxApi, sessionTxApi, clientsApi, matchesApi, sessionsApi } from '../../lib/api';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export function TrialBalanceDetailDialog({ 
  open, 
  onClose, 
  clientId, 
  clientName,
  matchIds = [],
  sessionIds = [],
  trialBalanceItem = null
}) {
  const [loading, setLoading] = useState(false);
  const [matchTxns, setMatchTxns] = useState([]);
  const [sessionTxns, setSessionTxns] = useState([]);
  const [matches, setMatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all' | 'without_patti' | 'patti_only'
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (open && clientId) {
      fetchData();
    }
  }, [open, clientId, matchIds, sessionIds]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch matches and sessions info
      const [matchesData, sessionsData, clientsData] = await Promise.all([
        matchesApi.list(),
        sessionsApi.list(),
        clientsApi.list()
      ]);
      
      setMatches(matchesData);
      setSessions(sessionsData);
      setClients(clientsData);

      // Fetch match transactions ONLY for selected matches
      let allMatchTxns = [];
      if (matchIds.length > 0) {
        for (const matchId of matchIds) {
          try {
            const txns = await matchTxApi.list(matchId);
            allMatchTxns = [...allMatchTxns, ...txns.filter(t => t.client_id === clientId)];
          } catch (e) {
            console.error(`Failed to fetch txns for match ${matchId}:`, e);
          }
        }
      }
      setMatchTxns(allMatchTxns);

      // Fetch session transactions ONLY for selected sessions
      let allSessionTxns = [];
      if (sessionIds.length > 0) {
        for (const sessionId of sessionIds) {
          try {
            const txns = await sessionTxApi.list(sessionId);
            allSessionTxns = [...allSessionTxns, ...txns.filter(t => t.client_id === clientId)];
          } catch (e) {
            console.error(`Failed to fetch txns for session ${sessionId}:`, e);
          }
        }
      }
      setSessionTxns(allSessionTxns);

      // Fetch settlement entries for this client
      try {
        const response = await fetch(`${API_URL}/api/settlements/client/${clientId}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          // Filter settlements to only include selected matches/sessions
          const filteredSettlements = data.filter(s => 
            (matchIds.length > 0 && matchIds.includes(s.match_id)) ||
            (sessionIds.length > 0 && sessionIds.includes(s.session_id))
          );
          setSettlements(filteredSettlements);
        }
      } catch (e) {
        console.error('Failed to fetch settlements:', e);
      }

      // Expand all groups by default
      const allGroupIds = new Set();
      matchIds.forEach(id => allGroupIds.add(`match_${id}`));
      sessionIds.forEach(id => allGroupIds.add(`session_${id}`));
      setExpandedGroups(allGroupIds);

    } catch (error) {
      console.error('Failed to fetch transaction data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get client info
  const getClientInfo = (clientIdToFind) => {
    return clients.find(c => c.client_id === clientIdToFind);
  };

  // Get reference client name
  const getRefClientName = (refClientId) => {
    if (!refClientId) return '';
    const client = clients.find(c => c.client_id === refClientId);
    return client?.name || '';
  };

  // Group transactions by match/session - ONLY for selected items
  const groupedTransactions = useMemo(() => {
    const groups = [];

    // Process ONLY selected matches
    matchIds.forEach(matchId => {
      const match = matches.find(m => m.match_id === matchId);
      if (!match) return;
      
      const matchName = match.name || matchId;
      const txns = matchTxns.filter(t => t.match_id === matchId);
      
      // Get settlement info for this match
      const settlement = settlements.find(s => s.match_id === matchId && s.client_id === clientId);
      
      // Filter based on patti/ref commission
      let filteredTxns = txns;
      if (filter === 'without_patti') {
        filteredTxns = txns.filter(t => !t.is_patti && !t.reference_client_id);
      } else if (filter === 'patti_only') {
        filteredTxns = txns.filter(t => t.is_patti || t.reference_client_id);
      }

      // Calculate totals
      let totalWonAmt = 0;
      
      // If match is declared, calculate won amounts based on winning team
      if (match.status === 'DECLARED' && match.winning_team) {
        filteredTxns.forEach(tx => {
          // Calculate won amount based on position and winning team
          const isWinningTeam = tx.team === match.winning_team;
          const baseAmount = tx.rate * tx.amount;
          
          if (tx.mode === 'L') {
            // L (Lagana) mode: Client wins if their team wins
            totalWonAmt += isWinningTeam ? baseAmount : -tx.amount;
          } else {
            // K (Khana) mode: Client wins if their team loses
            totalWonAmt += isWinningTeam ? -baseAmount : tx.amount;
          }
        });
      }

      // Use settlement gross_amount if available
      const groupTotal = settlement?.gross_amount ?? totalWonAmt;

      groups.push({
        id: `match_${matchId}`,
        type: 'match',
        name: matchName,
        matchId,
        transactions: filteredTxns,
        total: groupTotal,
        settlement,
        isDeclared: match.status === 'DECLARED',
        winningTeam: match.winning_team,
        match
      });
    });

    // Process ONLY selected sessions
    sessionIds.forEach(sessionId => {
      const session = sessions.find(s => s.session_id === sessionId);
      if (!session) return;
      
      const sessionName = session.name || sessionId;
      const txns = sessionTxns.filter(t => t.session_id === sessionId);
      
      // Get settlement info for this session
      const settlement = settlements.find(s => s.session_id === sessionId && s.client_id === clientId);
      
      // Filter based on patti/ref commission
      let filteredTxns = txns;
      if (filter === 'without_patti') {
        filteredTxns = txns.filter(t => !t.is_patti && !t.reference_client_id);
      } else if (filter === 'patti_only') {
        filteredTxns = txns.filter(t => t.is_patti || t.reference_client_id);
      }

      // Calculate totals
      let totalWonAmt = 0;
      if (session.status === 'DECLARED' && session.declared_run !== undefined) {
        filteredTxns.forEach(tx => {
          const txRun = tx.run;
          const declaredRun = session.declared_run;
          const baseAmount = tx.rate * tx.amount;
          
          if (tx.mode === 'YES') {
            totalWonAmt += declaredRun >= txRun ? baseAmount : -tx.amount;
          } else {
            totalWonAmt += declaredRun < txRun ? baseAmount : -tx.amount;
          }
        });
      }

      const groupTotal = settlement?.gross_amount ?? totalWonAmt;

      groups.push({
        id: `session_${sessionId}`,
        type: 'session',
        name: sessionName,
        sessionId,
        transactions: filteredTxns,
        total: groupTotal,
        settlement,
        isDeclared: session.status === 'DECLARED',
        declaredRun: session.declared_run,
        session
      });
    });

    return groups;
  }, [matchTxns, sessionTxns, matches, sessions, settlements, filter, clientId, matchIds, sessionIds]);

  // Calculate won amount (before commission)
  const wonAmount = useMemo(() => {
    return groupedTransactions.reduce((sum, group) => sum + (group.total || 0), 0);
  }, [groupedTransactions]);

  // Calculate commission paid and received based on won amount and client settings
  const commissionSummary = useMemo(() => {
    let commissionPaid = 0;
    let commissionReceived = 0;
    
    // Get client info for commission rates
    const clientInfo = getClientInfo(clientId);
    
    // For regular (non-patti) clients:
    // - If they WIN (positive total), we pay them commission (deducted from their winnings)
    // - Commission is based on their match_user_commission rate
    if (clientInfo && !trialBalanceItem?.is_patti) {
      if (wonAmount > 0) {
        // Client is winning, calculate commission we pay to them
        const matchCommRate = clientInfo.match_user_commission || 0;
        const sessionCommRate = clientInfo.session_user_commission || 0;
        
        // Calculate commission for each group
        groupedTransactions.forEach(group => {
          if (group.total > 0) {
            const rate = group.type === 'match' ? matchCommRate : sessionCommRate;
            commissionPaid += Math.abs(group.total) * (rate / 100);
          }
        });
      }
    }
    
    // For patti clients, commission is received by company
    if (trialBalanceItem?.is_patti) {
      commissionReceived = Math.abs(trialBalanceItem.patti_commission_received || 0) + 
                           Math.abs(trialBalanceItem.patti_ref_commission_received || 0);
    }
    
    // Reference commission we pay to this client (if they are a reference for others)
    if (trialBalanceItem?.ref_commission_received) {
      commissionPaid += Math.abs(trialBalanceItem.ref_commission_received);
    }

    return { paid: commissionPaid, received: commissionReceived };
  }, [groupedTransactions, wonAmount, trialBalanceItem, clientId, clients]);

  // Calculate grand total (Won Amount - Commission Paid + Commission Received)
  const grandTotal = useMemo(() => {
    return wonAmount - commissionSummary.paid + commissionSummary.received;
  }, [wonAmount, commissionSummary]);

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Toggle all groups
  const toggleAllGroups = () => {
    if (allExpanded) {
      setExpandedGroups(new Set());
    } else {
      const allIds = new Set(groupedTransactions.map(g => g.id));
      setExpandedGroups(allIds);
    }
    setAllExpanded(!allExpanded);
  };

  // Calculate won amount for a transaction
  // L (Lagana) = Betting ON the team - wins if team wins
  // K (Khana) = Betting AGAINST the team - wins if team loses
  const calculateWonAmount = (tx, group) => {
    if (!group.isDeclared) return null;
    
    if (group.type === 'match') {
      const isWinningTeam = tx.team === group.winningTeam;
      const baseAmount = tx.rate * tx.amount;
      
      if (tx.mode === 'L') {
        // L (Lagana) mode: Client wins if their team wins
        // Win: +baseAmount, Lose: -amount
        return isWinningTeam ? baseAmount : -tx.amount;
      } else {
        // K (Khana) mode: Client wins if their team loses
        // Win: +amount, Lose: -baseAmount
        return isWinningTeam ? -baseAmount : tx.amount;
      }
    } else {
      // Session
      const txRun = tx.run;
      const declaredRun = group.declaredRun;
      const baseAmount = tx.rate * tx.amount;
      
      if (tx.mode === 'YES') {
        return declaredRun >= txRun ? baseAmount : -tx.amount;
      } else {
        return declaredRun < txRun ? baseAmount : -tx.amount;
      }
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 15;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Transaction Detail - ${clientName}`, 14, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${format(new Date(), 'dd-MM-yyyy HH:mm')}`, 14, yPos);
    yPos += 10;

    // Groups
    groupedTransactions.forEach(group => {
      if (group.transactions.length === 0) return;
      
      if (yPos > 180) {
        doc.addPage();
        yPos = 15;
      }

      // Group header
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`${group.type === 'match' ? 'Match' : 'Session'}: ${group.name} (Total = ${group.total.toFixed(2)})`, 14, yPos);
      yPos += 4;

      // Transaction table
      const tableData = group.transactions.map(tx => {
        const wonAmt = calculateWonAmount(tx, group);
        const clientInfo = getClientInfo(clientId);
        const hasCommission = clientInfo?.match_user_commission > 0 || clientInfo?.session_user_commission > 0;
        
        return [
          tx.is_confirmed ? '✓' : '',
          tx.created_at ? format(new Date(tx.created_at), 'dd-MM-yyyy HH:mm') : '-',
          tx.rate?.toFixed(4) || '-',
          tx.amount?.toFixed(0) || '-',
          tx.mode || '-',
          tx.team || tx.run || '-',
          wonAmt !== null ? wonAmt.toFixed(2) : '-',
          hasCommission ? '✓' : '',
          tx.is_patti ? '✓' : '',
          getRefClientName(tx.reference_client_id || clientInfo?.match_reference),
          group.settlement?.created_at ? format(new Date(group.settlement.created_at), 'dd-MM-yyyy HH:mm') : '-'
        ];
      });

      doc.autoTable({
        startY: yPos,
        head: [['Confirm', 'Trans Date', 'Rate', 'Amount', 'Mode', 'Team/Run', 'Won Amt', 'Comm.', 'Patti', 'Ref. Client', 'Declare Date']],
        body: tableData,
        styles: { fontSize: 7, cellPadding: 1 },
        headStyles: { fillColor: [100, 100, 100], textColor: 255, fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 14, right: 14 }
      });

      yPos = doc.lastAutoTable.finalY + 8;
    });

    // Commission summary
    if (yPos > 180) {
      doc.addPage();
      yPos = 15;
    }
    doc.setFontSize(10);
    doc.text(`Commission Paid: ${commissionSummary.paid.toFixed(2)}`, 14, yPos);
    doc.text(`Commission Received: ${commissionSummary.received.toFixed(2)}`, 100, yPos);
    yPos += 8;

    // Grand total
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Grand Total: ${grandTotal.toFixed(2)}`, pageWidth - 60, yPos);

    doc.save(`transaction_detail_${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 border-b px-4 py-3">
          <DialogHeader className="space-y-0">
            <DialogTitle className="text-lg font-semibold text-slate-700 dark:text-slate-200">
              Transaction Detail
            </DialogTitle>
          </DialogHeader>
        </div>

        {/* Control Bar */}
        <div className="border-b bg-slate-50 dark:bg-slate-900/50 px-4 py-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            {/* Left: Client Name & Collapse */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Client Name:</span>
                <span className="font-semibold text-primary">{clientName}</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={toggleAllGroups}
                className="h-8"
              >
                {allExpanded ? 'Collapse' : 'Expand'}
              </Button>
            </div>

            {/* Center: Filter Options */}
            <RadioGroup 
              value={filter} 
              onValueChange={setFilter}
              className="flex items-center gap-4 flex-wrap"
            >
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="all" id="filter-all" />
                <Label htmlFor="filter-all" className="text-sm cursor-pointer whitespace-nowrap">
                  Show All Records
                </Label>
              </div>
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="without_patti" id="filter-without" />
                <Label htmlFor="filter-without" className="text-sm cursor-pointer whitespace-nowrap">
                  Show All Records without Patti/Ref. Comm. Only
                </Label>
              </div>
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="patti_only" id="filter-patti" />
                <Label htmlFor="filter-patti" className="text-sm cursor-pointer whitespace-nowrap">
                  Show Patti/Ref. Comm. Only
                </Label>
              </div>
            </RadioGroup>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={exportToPDF}
                className="h-8 gap-1"
              >
                <FileText className="h-3.5 w-3.5" />
                PDF
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onClose(false)}
                className="h-8"
              >
                Exit
              </Button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-white dark:bg-slate-950">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="p-4">
              {/* Sort dropdown */}
              <div className="mb-3">
                <div className="inline-flex items-center gap-1 px-3 py-1.5 border rounded bg-white dark:bg-slate-900 text-sm">
                  <span>Match / Session Name</span>
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>

              {/* Table */}
              <div className="border rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="bg-slate-100 dark:bg-slate-800 border-b">
                  <div className="grid grid-cols-12 text-xs font-semibold">
                    {/* Transaction Group */}
                    <div className="col-span-6 border-r">
                      <div className="px-2 py-1.5 text-center border-b bg-slate-200 dark:bg-slate-700">
                        Transaction
                      </div>
                      <div className="grid grid-cols-6">
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Is Confirm</div>
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Trans Date</div>
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Rate</div>
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Amount</div>
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Mode</div>
                        <div className="px-1 py-1.5 text-center text-[11px]">Team / Run</div>
                      </div>
                    </div>
                    {/* Declare Group */}
                    <div className="col-span-6">
                      <div className="px-2 py-1.5 text-center border-b bg-slate-200 dark:bg-slate-700">
                        Declare
                      </div>
                      <div className="grid grid-cols-5">
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Won Amt</div>
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Comm.</div>
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Patti</div>
                        <div className="px-1 py-1.5 text-center border-r text-[11px]">Ref. Client</div>
                        <div className="px-1 py-1.5 text-center text-[11px]">Date</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Body */}
                <div className="divide-y">
                  {groupedTransactions.map(group => {
                    // Get client info for commission check
                    const clientInfo = getClientInfo(clientId);
                    const hasCommission = group.type === 'match' 
                      ? (clientInfo?.match_user_commission > 0)
                      : (clientInfo?.session_user_commission > 0);
                    const refClientId = group.type === 'match'
                      ? clientInfo?.match_reference
                      : clientInfo?.session_reference;

                    return (
                      <div key={group.id}>
                        {/* Group Header Row */}
                        <div 
                          className="flex items-center gap-2 px-2 py-2 bg-slate-50 dark:bg-slate-900/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 border-b"
                          onClick={() => toggleGroup(group.id)}
                        >
                          <button className="p-0.5">
                            {expandedGroups.has(group.id) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                            Match / Session Name: {group.name}
                            <span className={cn(
                              "ml-2 font-semibold",
                              group.total >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'
                            )}>
                              (Total = {group.total.toFixed(2)})
                            </span>
                          </span>
                        </div>

                        {/* Group Transactions */}
                        {expandedGroups.has(group.id) && group.transactions.length > 0 && (
                          <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {group.transactions.map((tx, idx) => {
                              const wonAmt = calculateWonAmount(tx, group);
                              const isPatti = tx.is_patti;
                              const txRefClient = tx.reference_client_id || refClientId;

                              return (
                                <div 
                                  key={tx.txn_id || idx} 
                                  className="grid grid-cols-12 text-xs hover:bg-blue-50/50 dark:hover:bg-blue-950/20"
                                >
                                  {/* Transaction columns */}
                                  <div className="col-span-6 grid grid-cols-6 border-r">
                                    {/* Is Confirm */}
                                    <div className="px-1 py-2 flex items-center justify-center border-r">
                                      <Checkbox 
                                        checked={tx.is_confirmed || false}
                                        disabled
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    {/* Trans Date */}
                                    <div className="px-1 py-2 text-center border-r whitespace-nowrap text-[11px]">
                                      {tx.created_at ? format(new Date(tx.created_at), 'dd-MM-yyyy HH:mm') : '-'}
                                    </div>
                                    {/* Rate */}
                                    <div className="px-1 py-2 text-center border-r font-mono text-[11px]">
                                      {tx.rate?.toFixed(4) || '-'}
                                    </div>
                                    {/* Amount */}
                                    <div className="px-1 py-2 text-center border-r font-mono text-[11px]">
                                      {tx.amount?.toFixed(0) || '-'}
                                    </div>
                                    {/* Mode */}
                                    <div className="px-1 py-2 text-center border-r">
                                      <span className={cn(
                                        "px-1.5 py-0.5 rounded text-[10px] font-medium",
                                        tx.mode === 'K' || tx.mode === 'YES' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      )}>
                                        {tx.mode}
                                      </span>
                                    </div>
                                    {/* Team / Run */}
                                    <div className="px-1 py-2 text-center text-[11px]">
                                      {tx.team || tx.run || '-'}
                                    </div>
                                  </div>

                                  {/* Declare columns */}
                                  <div className="col-span-6 grid grid-cols-5">
                                    {/* Won Amt */}
                                    <div className={cn(
                                      "px-1 py-2 text-center border-r font-mono font-medium text-[11px]",
                                      wonAmt === null ? '' :
                                      wonAmt >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'
                                    )}>
                                      {wonAmt !== null ? wonAmt.toFixed(2) : '-'}
                                    </div>
                                    {/* Comm. */}
                                    <div className="px-1 py-2 flex items-center justify-center border-r">
                                      <Checkbox 
                                        checked={hasCommission}
                                        disabled
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    {/* Patti */}
                                    <div className="px-1 py-2 flex items-center justify-center border-r">
                                      <Checkbox 
                                        checked={isPatti}
                                        disabled
                                        className="h-4 w-4"
                                      />
                                    </div>
                                    {/* Ref. Client */}
                                    <div className="px-1 py-2 text-center border-r text-purple-600 dark:text-purple-400 text-[11px]">
                                      {getRefClientName(txRefClient)}
                                    </div>
                                    {/* Declare Date */}
                                    <div className="px-1 py-2 text-center whitespace-nowrap text-[11px]">
                                      {group.settlement?.created_at 
                                        ? format(new Date(group.settlement.created_at), 'dd-MM-yyyy HH:mm')
                                        : '-'
                                      }
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Empty state for group */}
                        {expandedGroups.has(group.id) && group.transactions.length === 0 && (
                          <div className="py-4 text-center text-muted-foreground text-sm">
                            No transactions for this {group.type}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Empty state */}
                  {groupedTransactions.length === 0 && (
                    <div className="py-12 text-center text-muted-foreground">
                      No transactions found for selected matches/sessions
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with Commission Summary and Grand Total */}
        <div className="border-t bg-slate-50 dark:bg-slate-900/50 px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Commission Summary */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Commission Paid:</span>
                <span className="font-mono font-semibold text-red-600 dark:text-red-400">
                  {commissionSummary.paid.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Commission Received:</span>
                <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                  {commissionSummary.received.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Grand Total */}
            <div className="bg-white dark:bg-slate-800 border rounded px-6 py-2 shadow-sm">
              <span className="text-sm text-muted-foreground mr-2">Grand Total:</span>
              <span className={cn(
                "text-lg font-bold font-mono",
                grandTotal >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
              )}>
                {grandTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
